/**
 * attachmentHealOnHydrate — a record that still has ONLY its base64 copy is re-uploaded the
 * next time the OWNER's browser shows it (chat-thread PR-6, 2026-09-09).
 *
 * Why: the send path retries a failed upload in-session (60 s, then 5 min) — but a tab
 * that was closed, a phone that slept, a laptop that lost its connection never got those
 * retries, and such a record is the FIRST to vanish: the server transcript strips inline
 * payloads (Firestore's 1 MiB doc limit), so the picture lives in this browser's local
 * storage only, invisible on every other device and to every other participant. When
 * the message hydrates here again, the bytes are still at hand — upload them, merge the
 * won locators in (additively, never clobbering a richer entry), patch state and the
 * per-thread storage exactly the way the send path does.
 *
 * Bounds: only the viewer's OWN user turns (never someone else's file); a few per pass;
 * ONE attempt per record per session (the next reload tries again); never while offline.
 * Pure planning + a runner with injected I/O so the unit test drives it without Storage.
 * Lever: REACT_APP_ATTACHMENT_HEAL_ON_HYDRATE=off.
 */
import type { ChatMessage, SerializedAttachment } from './types';
import { hasDurableAttachmentLocator } from './attachmentRecords';

export const ATTACHMENT_HEAL_ON_HYDRATE_DELAY_MS = 2_500;
export const ATTACHMENT_HEAL_ON_HYDRATE_MAX_PER_PASS = 3;

export interface HealCandidate {
  messageId: string;
  name: string;
  type: string;
  dataUrl: string;
}

export function attachmentHealOnHydrateEnabled(env: Record<string, string | undefined> = process.env): boolean {
  return String(env.REACT_APP_ATTACHMENT_HEAL_ON_HYDRATE || '').trim().toLowerCase() !== 'off';
}

/** The viewer's own user turn: their bubble, or a turn with no sender stamped (solo threads). */
export function isViewersOwnUserMessage(m: ChatMessage | null | undefined, uid: string): boolean {
  if (!m || m.isUser !== true) return false;
  const sender = typeof m.senderId === 'string' ? m.senderId.trim() : '';
  return !sender || sender === uid;
}

/** A record that has bytes to re-upload and nothing durable yet. */
export function needsLocatorHeal(att: SerializedAttachment | null | undefined): boolean {
  return !!(att && typeof att.dataUrl === 'string' && att.dataUrl.startsWith('data:') && !hasDurableAttachmentLocator(att));
}

export function healKey(c: { messageId: string; name: string }): string {
  return `${c.messageId}|${c.name}`;
}

/**
 * Newest first (the most valuable records), the viewer's own user turns only, records
 * with a base64 copy and no durable locator, not yet attempted this session, bounded.
 */
export function attachmentHealCandidates(
  messages: ReadonlyArray<ChatMessage> | null | undefined,
  uid: string,
  attempted: ReadonlySet<string>,
  max: number = ATTACHMENT_HEAL_ON_HYDRATE_MAX_PER_PASS,
): HealCandidate[] {
  if (!Array.isArray(messages) || !uid || max <= 0) return [];
  const out: HealCandidate[] = [];
  for (let i = messages.length - 1; i >= 0 && out.length < max; i -= 1) {
    const m = messages[i];
    if (!isViewersOwnUserMessage(m, uid) || !m.id) continue;
    const list = Array.isArray(m.attachmentsSerialized) ? m.attachmentsSerialized : [];
    for (const att of list) {
      if (out.length >= max) break;
      if (!att?.name || !needsLocatorHeal(att)) continue;
      const candidate: HealCandidate = { messageId: m.id, name: att.name, type: att.type || '', dataUrl: att.dataUrl as string };
      if (attempted.has(healKey(candidate))) continue;
      out.push(candidate);
    }
  }
  return out;
}

/** The base64 copy back into a File the upload lane accepts. Null when the browser cannot. */
export async function fileFromDataUrl(c: HealCandidate): Promise<File | null> {
  try {
    if (typeof fetch !== 'function' || typeof File === 'undefined') return null;
    const res = await fetch(c.dataUrl);
    const blob = await res.blob();
    return new File([blob], c.name, { type: c.type || blob.type || 'application/octet-stream' });
  } catch {
    return null;
  }
}

/**
 * The won locators merged onto the records that lacked one, by name — additive: a record
 * keeps its own fields (the base64 copy included, it is the instant local download) and
 * gains only what it did not have. Null when nothing changed.
 */
export function mergeHealedLocators(
  list: ReadonlyArray<SerializedAttachment> | null | undefined,
  healedByName: ReadonlyMap<string, SerializedAttachment>,
): SerializedAttachment[] | null {
  if (!Array.isArray(list) || list.length === 0 || healedByName.size === 0) return null;
  let changed = false;
  const next = list.map((att) => {
    if (!att?.name || !needsLocatorHeal(att)) return att;
    const won = healedByName.get(att.name);
    if (!won || !hasDurableAttachmentLocator(won)) return att;
    changed = true;
    return {
      ...att,
      ...(won.storageUrl ? { storageUrl: won.storageUrl } : {}),
      ...(won.storagePath ? { storagePath: won.storagePath } : {}),
      ...(won.gcsPath ? { gcsPath: won.gcsPath } : {}),
      ...(typeof won.storageUrlMintedAt === 'number' ? { storageUrlMintedAt: won.storageUrlMintedAt } : {}),
    };
  });
  return changed ? next : null;
}

export interface HealOnHydrateIo {
  candidates: HealCandidate[];
  attempted: Set<string>;
  /** The storage upload lane (serializeAttachmentsWithStorage, bound to the uid). */
  upload: (files: File[], messageId: string) => Promise<SerializedAttachment[]>;
  /** The CURRENT message list of the thread the candidates came from (storage first, state as fallback). */
  readMessages: () => ChatMessage[];
  /** The per-thread storage write (the store mirrors it to Firestore). */
  writeMessages: (next: ChatMessage[]) => void;
  /** The live state patch — the caller decides whether the thread is still on screen. */
  patchState: (messageId: string, attachmentsSerialized: SerializedAttachment[]) => void;
  toFile?: (c: HealCandidate) => Promise<File | null>;
}

/** Runs one pass. Every candidate is marked attempted up front; one message's failure never blocks the next. */
export async function runAttachmentHealOnHydrate(io: HealOnHydrateIo): Promise<{ attempted: number; healed: number }> {
  const byMessage = new Map<string, HealCandidate[]>();
  for (const c of io.candidates) {
    io.attempted.add(healKey(c));
    const list = byMessage.get(c.messageId) ?? [];
    list.push(c);
    byMessage.set(c.messageId, list);
  }
  const toFile = io.toFile ?? fileFromDataUrl;
  let healed = 0;
  for (const [messageId, list] of byMessage) {
    try {
      const files = (await Promise.all(list.map((c) => toFile(c)))).filter((f): f is File => !!f);
      if (files.length === 0) continue;
      const won = await io.upload(files, messageId);
      const byName = new Map<string, SerializedAttachment>();
      for (const w of Array.isArray(won) ? won : []) {
        if (w?.name && hasDurableAttachmentLocator(w)) byName.set(w.name, w);
      }
      if (byName.size === 0) continue;
      const current = io.readMessages();
      const idx = Array.isArray(current) ? current.findIndex((m) => m?.id === messageId) : -1;
      if (idx === -1) continue;
      const merged = mergeHealedLocators(current[idx].attachmentsSerialized, byName);
      if (!merged) continue;
      const next = current.slice();
      next[idx] = { ...current[idx], attachmentsSerialized: merged };
      io.writeMessages(next);
      io.patchState(messageId, merged);
      healed += byName.size;
    } catch {
      // the next reload retries this message; nothing else is held up
    }
  }
  return { attempted: io.candidates.length, healed };
}
